import { Component, OnInit, OnDestroy, Output, EventEmitter, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { FullViewService } from '../../services/full-view.service';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AttachmentViewModalComponent } from '../../../../../_shared/components/modals/attachment-view-modal/attachment-view-modal.component';
import { MaintenanceCostingService } from '../../services/maintenance-costing.service';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { MaintenanceCostingRequest } from '../../models/maintenance-costing-request.model';
import { GetAncillaryCostResponse } from '../../models/maintenance-costing-response.model';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-ancillary-cost',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    PaginationComponent,
    EmptyStateComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './ancillary-cost.component.html',
  styleUrl: './ancillary-cost.component.scss'
})
export class AncillaryCostComponent implements OnInit, OnDestroy {
  @Output() fullViewChange = new EventEmitter<boolean>();

  private readonly fullViewService = inject(FullViewService);
  private readonly costingService = inject(MaintenanceCostingService);
  private readonly filterService = inject(FilterService);
  private readonly modalService = inject(NgbModal);
  private cancelSubject: Subject<void> = new Subject<void>();
  private invoiceCancelSubject: Subject<void> = new Subject<void>();
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private widgetFilter: WidgetFilterData = {} as WidgetFilterData;

  readonly previewPageSize: number = 5;
  readonly fullViewPageSize: number = 12;

  searchTerm: string = '';
  isLoading: boolean = true;
  isFullView: boolean = this.fullViewService.isFullView();
  currencySymbol: string = '';
  currencyCode: string = '';
  distanceSymbol: string = '';

  costs: GetAncillaryCostResponse[] = [];

  page: number = 1;
  totalPages: number = 0;
  totalCount: number = 0;

  constructor() {
    const userSettings = this.authService.getUserSettings();
    this.currencyCode = userSettings?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
    this.distanceSymbol = userSettings?.distanceSymbol ?? 'km';

    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe((filter) => {
        this.widgetFilter = filter;
        this.page = 1;
        this.loadCosts();
      });

    effect(() => {
      const active = this.fullViewService.isFullView();

      if (this.isFullView !== active) {
        this.isFullView = active;
        this.page = 1;

        // The preview and the full view request different page sizes, so the rows have to be fetched again
        this.loadCosts();
      }
    });
  }

  get pageSize(): number {
    return this.isFullView ? this.fullViewPageSize : this.previewPageSize;
  }

  get showingCount(): number {
    return Math.min(this.page * this.pageSize, this.totalCount);
  }

  async ngOnInit(): Promise<void> {
    this.widgetFilter = this.filterService.filter;
    await this.loadCosts();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
    this.invoiceCancelSubject.next();
    this.invoiceCancelSubject.complete();
  }

  private async loadCosts(): Promise<void> {
    const { accountIds, startDate, endDate } = this.widgetFilter;

    // The page only publishes its filters once both the account and date filters have initialised
    if (!startDate || !endDate) {
      return;
    }

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: MaintenanceCostingRequest = {
        accountIds: accountIds ?? [],
        timeFrom: startDate,
        timeTo: endDate,
        page: this.page,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm
      };

      const response = await this.costingService.getAncillaryCostRequest(request, this.cancelSubject);
      this.costs = response.data?.results ?? [];
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.costs = [];
          this.totalCount = 0;
          this.totalPages = 0;
          this.isLoading = false;
        }
      });
    }
  }

  reload(): void {
    this.page = 1;
    this.loadCosts();
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.page = 1;
    this.loadCosts();
  }

  goToPage(page: number): void {
    this.page = page;
    this.loadCosts();
  }

  onViewAll(): void {
    this.fullViewService.enableFullView();
  }

  onBack(): void {
    this.fullViewService.disableFullView();
  }

  async onViewInvoice(cost: GetAncillaryCostResponse): Promise<void> {
    const modalRef = this.modalService.open(AttachmentViewModalComponent, { centered: true, size: 'lg' });
    modalRef.componentInstance.fileUrl = cost.invoiceUrl;
    modalRef.componentInstance.fileName = cost.invoiceFileName ?? '';

    try {
      // invoiceUrl carries no attachment disposition, so the download link is fetched to save the file under its original name
      const response = await this.costingService.downloadInvoiceRequest(cost.id, this.invoiceCancelSubject);
      modalRef.componentInstance.downloadUrl = response.data ?? null;
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  formatCurrency(value: number): string {
    return `${this.currencySymbol}${value.toLocaleString()}`;
  }

  formatOdometer(value: number | null): string {
    return value === null ? '-' : `${value.toLocaleString()} ${this.distanceSymbol}`;
  }
}